import { useState } from "react";
import { Link } from "react-router-dom";
import useSeo from "@/hooks/useSeo";
import Breadcrumb from "@/components/common/Breadcrumb";

const faqs = [
  {
    q: "漫荒是什么？为什么选择我爱漫画网？",
    a: "漫荒指想看漫画却不知道该看什么的时刻。我爱漫画网通过补番清单、高分漫画、冷门佳作与必看榜单等栏目，按口碑和类型帮你快速锁定下一本好漫画。",
  },
  {
    q: "榜单上的评分是怎么来的？",
    a: "评分综合了国内外主要漫画平台的长期口碑数据与读者反馈，仅作参考。是否好看最终还要看你的个人口味，我们更希望推荐理由能帮你做判断。",
  },
  {
    q: "补番清单多久更新一次？",
    a: "我们每周整理并更新一期补番清单，按主题、类型或热门程度打包推荐，方便你整段安排阅读计划，也让收藏夹始终有内容可追。",
  },
  {
    q: "推荐会不会剧透？",
    a: "不会。所有推荐内容只介绍作品背景与值得看的理由，严格避免关键剧情与结局剧透，请放心浏览。",
  },
  {
    q: "可以投稿推荐自己喜欢的漫画吗？",
    a: "当然可以。欢迎通过「联系我们」页面投稿，说明作品名称、作者与推荐理由，我们会试读审核后补充进对应栏目与榜单。",
  },
  {
    q: "为什么有的冷门佳作热度不高？",
    a: "冷门佳作往往因为宣发少、题材小众或画风独特而不够出圈，但质量与口碑并不输热门作品。我们专门设了冷门佳作栏目，把这些宝藏打捞出来。",
  },
  {
    q: "网站可以按类型或关键词查找作品吗？",
    a: "可以。推荐榜单页支持按栏目筛选，也支持搜索作品名、作者、类型或标签，方便你精准找到想看的漫画。",
  },
  {
    q: "如何联系我爱漫画网？",
    a: "你可以通过「联系我们」页面提交信息，也可以在页面底部找到我们的联系邮箱，我们会尽快回复。",
  },
];

export default function Faq() {
  useSeo({
    title: "常见问题 - 我爱漫画网 | 漫荒推荐 · 补番清单",
    description:
      "关于漫荒、漫画评分、补番清单更新、投稿推荐与联系我们等常见问题解答，帮助你更好地使用我爱漫画网找到好漫画。",
    path: "/faq",
  });

  const [open, setOpen] = useState<number>(0);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />

      <section className="bg-background-100">
        <div className="container-page flex flex-col items-center py-12 text-center md:py-16">
          <span className="mb-3 inline-flex items-center gap-2 rounded-full bg-accent-100 px-4 py-1.5 text-sm font-medium text-accent-700">
            <i className="ri-question-line" />
            常见问题
          </span>
          <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-4xl">你可能关心的问题</h1>
          <p className="mt-3 max-w-xl text-sm leading-relaxed text-foreground-600 md:text-base">
            关于漫荒、评分、清单更新、投稿与联系方式的常见疑问，这里都有答案。
          </p>
        </div>
      </section>

      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "常见问题" }]} />

      <section className="container-page pb-12 md:pb-16">
        <div className="mx-auto max-w-3xl">
          <ul className="divide-y divide-background-200 overflow-hidden rounded-lg border border-background-200 bg-background-50">
            {faqs.map((f, i) => {
              const isOpen = open === i;
              return (
                <li key={f.q}>
                  <button
                    type="button"
                    onClick={() => setOpen(isOpen ? -1 : i)}
                    className="flex w-full items-center gap-3 px-5 py-4 text-left md:px-6"
                    aria-expanded={isOpen}
                  >
                    <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold ${isOpen ? "bg-primary-500 text-background-50" : "bg-primary-100 text-primary-600"}`}>
                      {i + 1}
                    </span>
                    <span className="flex-1 text-sm font-bold text-foreground-950 md:text-base">{f.q}</span>
                    <i className={`shrink-0 text-lg text-foreground-500 transition-transform ${isOpen ? "rotate-45" : ""}`}>
                      {isOpen ? "ri-add-line" : "ri-add-line"}
                    </i>
                  </button>
                  <div className={`grid transition-all duration-300 ${isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
                    <div className="overflow-hidden">
                      <p className="px-5 pb-5 pl-[4.75rem] pr-6 text-sm leading-relaxed text-foreground-600 md:px-6 md:pl-20">
                        {f.a}
                      </p>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>

          <div className="mt-8 flex flex-col items-center gap-3 rounded-lg bg-secondary-100 px-6 py-8 text-center">
            <h2 className="font-heading text-lg font-bold text-foreground-950">还有问题没解决？</h2>
            <p className="text-sm text-foreground-600">欢迎直接联系我们，我们会尽快为你解答。</p>
            <Link to="/contact" className="mt-1 inline-flex items-center gap-2 rounded-full bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600">
              <i className="ri-mail-send-line" />
              联系我们
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}